// "About you" answers shared across calculators, remembered in this browser only.
import { useEffect, useState } from "react";
import type { FilingStatus } from "@ftr/tax-core";

export interface Profile {
  status: FilingStatus;
  /** Your W-2 box 1. The calculators treat the prop or futures activity as yours. */
  wages: string;
  /** Your W-2 boxes 3 + 7 and box 5, when they differ from box 1. Blank means "same as box 1". */
  ssWages: string;
  medicareWages: string;
  /** Joint returns only: your spouse's W-2 box 1 and box 5. */
  spouseWages: string;
  spouseMedicareWages: string;
  withholding: string;
  interest: string;
  otherIncome: string;
  /** "yes" | "no" | "unsure" | "" (unanswered) */
  specifiedService: string;
}

const KEY = "ftr.profile.v1";
const EMPTY: Profile = {
  status: "single",
  wages: "",
  ssWages: "",
  medicareWages: "",
  spouseWages: "",
  spouseMedicareWages: "",
  withholding: "",
  interest: "",
  otherIncome: "",
  specifiedService: "",
};

function load(): Profile {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...EMPTY, ...JSON.parse(raw) } : EMPTY;
  } catch {
    return EMPTY;
  }
}

export function useProfile(): [Profile, (p: Partial<Profile>) => void] {
  const [profile, setProfile] = useState<Profile>(load);
  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(profile));
    } catch {
      /* storage unavailable: keep working without it */
    }
  }, [profile]);
  return [profile, (p) => setProfile((cur) => ({ ...cur, ...p }))];
}

/** Maps the user's answer to the core's input. "Not sure" uses the answer that gives the higher tax. */
export function specifiedServiceAnswer(v: string): boolean | null {
  if (v === "yes" || v === "unsure") return true;
  if (v === "no") return false;
  return null;
}
